import { useState, type FormEvent } from "react";
import Seo from "../components/Seo.tsx";
import Divider from "../components/ornaments/Divider.tsx";
import Feather from "../components/ornaments/Feather.tsx";
import { Button } from "../components/ui/Button.tsx";
import { Field, Input } from "../components/ui/Field.tsx";

const IG_URL = "https://www.instagram.com/bead0re/";
const IG_HANDLE = "@bead0re";
const EMAIL = "Hello@beadorecandles.com";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSending(true);
    try {
      await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
        }),
      });
      setSent(true);
    } catch {
      setSent(true);
    }
    setSending(false);
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-14 md:px-10 md:py-20">
      <Seo
        title="Contact Us"
        description="Get in touch with BeAdoré Candles — custom orders, inquiries, collaborations. Email hello@beadorecandles.com or visit us on Instagram @bead0re."
        path="/contact"
      />

      {/* Hero */}
      <section className="marble-panel relative mb-20 overflow-hidden px-8 py-20 text-center md:px-16 md:py-28">
        <Feather
          className="pointer-events-none absolute top-1/2 left-1/2 h-[520px] w-auto -translate-x-1/2 -translate-y-1/2 opacity-[0.07]"
          style={{ animation: "var(--animate-fade-in)" }}
        />
        <div className="relative" style={{ animation: "var(--animate-fade-up)" }}>
          <span className="eyebrow">Reach the Atelier</span>
          <h1 className="mt-6 font-serif text-5xl leading-[1.05] text-[var(--color-emerald-deep)] md:text-6xl">
            We'd love to{" "}
            <em className="text-gold-leaf font-serif italic">hear from you.</em>
          </h1>
          <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-[var(--color-charcoal)]/75">
            Custom orders, bulk inquiries, collaborations, or just to say hello
            — we answer every message ourselves.
          </p>
        </div>
      </section>

      <div className="grid gap-16 md:grid-cols-2">
        {/* Left — Contact form + email */}
        <section>
          <h2 className="font-serif text-3xl text-[var(--color-emerald-deep)]">
            Send Us a Message
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[var(--color-stone)]">
            For custom candle requests, event orders (like{" "}
            <strong className="text-[var(--color-emerald-deep)]">
              Bloomscape
            </strong>{" "}
            floral bouquets), personalized gifts, or any question at all — fill
            out the form or email us directly.
          </p>

          <a
            href={`mailto:${EMAIL}`}
            className="mt-6 inline-flex items-center gap-3 rounded border border-[var(--color-emerald)]/40 px-6 py-3.5 text-[0.72rem] font-medium uppercase tracking-[0.22em] text-[var(--color-emerald-deep)] transition-all duration-300 hover:border-[var(--color-gold)] hover:text-[var(--color-emerald)]"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <rect
                x="2"
                y="4"
                width="20"
                height="16"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M2 7l10 6 10-6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {EMAIL}
          </a>

          <Divider className="my-10" />

          {sent ? (
            <div className="rounded border border-[var(--color-sage)] bg-[var(--color-sage-light)]/20 px-8 py-10 text-center">
              <p className="font-serif text-2xl italic text-[var(--color-emerald-deep)]">
                Message sent — we'll be in touch.
              </p>
              <p className="mt-3 text-sm text-[var(--color-stone)]">
                We typically respond within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-8">
              <Field label="Your name" htmlFor="contact-name">
                <Input
                  id="contact-name"
                  type="text"
                  placeholder="e.g. Sarah"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={100}
                  disabled={sending}
                />
              </Field>
              <Field label="Your email" htmlFor="contact-email">
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="you@somewhere.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={254}
                  disabled={sending}
                />
              </Field>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="contact-message"
                  className="font-serif text-base italic text-[var(--color-forest-deep)]"
                >
                  Your message
                </label>
                <textarea
                  id="contact-message"
                  rows={5}
                  placeholder="Tell us what you're looking for — custom scents, event orders, gift ideas, or anything else."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  maxLength={2000}
                  disabled={sending}
                  className="w-full resize-none border-0 border-b border-[var(--color-rule)] bg-transparent px-1 py-3 font-serif text-lg text-[var(--color-charcoal)] placeholder:text-[var(--color-muted)]/70 focus:border-[var(--color-forest)] focus:outline-none"
                />
              </div>
              <Button type="submit" disabled={sending}>
                {sending ? "Sending…" : "Send Message"}
              </Button>
            </form>
          )}
        </section>

        {/* Right — Instagram + info */}
        <section>
          <h2 className="font-serif text-3xl text-[var(--color-emerald-deep)]">
            Follow the Atelier
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[var(--color-stone)]">
            Behind-the-scenes pours, new releases, and the quiet beauty of
            hand-crafted wax. Follow us on Instagram for the latest from BeAdoré.
          </p>

          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-3 rounded border border-[var(--color-emerald)]/40 px-6 py-3.5 text-[0.72rem] font-medium uppercase tracking-[0.22em] text-[var(--color-emerald-deep)] transition-all duration-300 hover:border-[var(--color-gold)] hover:text-[var(--color-emerald)]"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle
                cx="12"
                cy="12"
                r="5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
            </svg>
            {IG_HANDLE}
          </a>

          <Divider className="my-10" />

          {/* Instagram CTA */}
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded border border-[var(--color-rule)] bg-[var(--color-emerald-deep)] transition-all duration-500 hover:shadow-[0_30px_60px_-30px_rgba(15,71,49,0.6)]"
          >
            <div className="px-8 py-12 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border-2 border-[var(--color-gold)]/40 bg-[var(--color-emerald)]/30">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-[var(--color-gold-soft)]"
                >
                  <rect
                    x="2"
                    y="2"
                    width="20"
                    height="20"
                    rx="5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
                </svg>
              </div>
              <p className="text-[0.65rem] font-medium uppercase tracking-[0.28em] text-[var(--color-sage-light)]">
                Follow the Atelier on Instagram
              </p>
              <p className="mt-4 font-serif text-3xl italic text-[var(--color-cream)] md:text-4xl">
                {IG_HANDLE}
              </p>
              <p className="mx-auto mt-5 max-w-sm text-sm leading-relaxed text-[var(--color-cream)]/65">
                Behind-the-scenes pours, new releases, candle care tips, and the
                quiet beauty of hand-crafted wax. See what we're making next.
              </p>
              <div className="mt-8 inline-flex items-center gap-2 border border-[var(--color-gold)]/40 px-7 py-3 text-[0.7rem] font-medium uppercase tracking-[0.22em] text-[var(--color-gold-soft)] transition-all duration-300 group-hover:border-[var(--color-gold)] group-hover:text-[var(--color-gold)]">
                Open Instagram
                <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </div>
            </div>
          </a>

          <Divider className="my-10" />

          {/* Business info */}
          <div className="space-y-5 text-sm text-[var(--color-stone)]">
            <div>
              <p className="text-[0.62rem] font-medium uppercase tracking-[0.22em] text-[var(--color-amber)]">
                Email
              </p>
              <a
                href={`mailto:${EMAIL}`}
                className="mt-1 block text-[var(--color-emerald-deep)] underline decoration-[var(--color-rule)] underline-offset-4 hover:decoration-[var(--color-gold)]"
              >
                {EMAIL}
              </a>
            </div>
            <div>
              <p className="text-[0.62rem] font-medium uppercase tracking-[0.22em] text-[var(--color-amber)]">
                Instagram
              </p>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 block text-[var(--color-emerald-deep)] underline decoration-[var(--color-rule)] underline-offset-4 hover:decoration-[var(--color-gold)]"
              >
                {IG_HANDLE}
              </a>
            </div>
            <div>
              <p className="text-[0.62rem] font-medium uppercase tracking-[0.22em] text-[var(--color-amber)]">
                Response Time
              </p>
              <p className="mt-1 text-[var(--color-charcoal)]">
                We typically respond within 24 hours
              </p>
            </div>
            <div>
              <p className="text-[0.62rem] font-medium uppercase tracking-[0.22em] text-[var(--color-amber)]">
                Custom Orders
              </p>
              <p className="mt-1 text-[var(--color-charcoal)]">
                Bloomscape bouquets, bulk party favours, bespoke scent blends —
                please allow 2 weeks lead time for custom pieces
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
